import React, { useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Star, Sparkles, Quote } from 'lucide-react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { testimonials } from '../mockData';

gsap.registerPlugin(ScrollTrigger);

export const Testimonials = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const gridRef = useRef(null);
  const ratingRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(headerRef.current?.children || [], {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
        scrollTrigger: { trigger: headerRef.current, start: 'top 80%' },
      });

      gsap.from(gridRef.current?.querySelectorAll('.testimonial-tile') || [], {
        y: 60,
        opacity: 0,
        duration: 0.8,
        stagger: 0.15,
        ease: 'power3.out',
        scrollTrigger: { trigger: gridRef.current, start: 'top 80%' },
      });

      gsap.from(ratingRef.current, {
        scale: 0.85,
        opacity: 0,
        duration: 0.8,
        ease: 'back.out(1.4)',
        scrollTrigger: { trigger: ratingRef.current, start: 'top 90%' },
      });
    }, sectionRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      data-testid="testimonials-section"
      className="py-24 bg-gradient-to-b from-white to-pink-50/30 relative overflow-hidden"
    >
      <div className="absolute -left-32 top-1/3 w-96 h-96 bg-pink-200/20 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <div ref={headerRef} className="text-center mb-14">
          <div className="inline-flex items-center gap-2 bg-pink-100 px-4 py-2 rounded-full mb-5">
            <Sparkles className="w-4 h-4 text-pink-600" />
            <span className="text-sm font-medium text-pink-700">Testimonials</span>
          </div>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-5 bg-gradient-to-r from-pink-600 to-rose-500 bg-clip-text text-transparent leading-tight">
            What Our Clients Say
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Hundreds of happy clients. Here's why they keep coming back.
          </p>
        </div>

        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {testimonials.map((testimonial) => (
            <Card
              key={testimonial.id}
              data-testid={`testimonial-card-${testimonial.id}`}
              className="testimonial-tile relative border-none shadow-md hover:shadow-2xl hover:shadow-pink-200/40 transition-all duration-500 hover:-translate-y-2 bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden"
            >
              <div className="absolute top-4 right-4 opacity-10">
                <Quote className="w-16 h-16 text-pink-600" />
              </div>
              <CardContent className="pt-7 relative">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, index) => (
                    <Star
                      key={index}
                      className={`w-4 h-4 ${
                        index < testimonial.rating
                          ? 'fill-pink-500 text-pink-500'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <p className="text-gray-700 mb-5 text-sm leading-relaxed italic">
                  "{testimonial.text}"
                </p>
                <div className="pt-3 border-t border-pink-100">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{testimonial.date}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div ref={ratingRef} className="mt-14 text-center">
          <div
            data-testid="overall-rating-badge"
            className="inline-flex items-center gap-3 bg-gradient-to-r from-pink-100 to-rose-100 px-7 py-4 rounded-full shadow-lg border border-pink-200"
          >
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-5 h-5 fill-pink-600 text-pink-600" />
              ))}
            </div>
            <span className="text-2xl font-bold text-gray-800">4.8</span>
            <span className="text-gray-600 text-sm">from 38 reviews</span>
          </div>
        </div>
      </div>
    </section>
  );
};
